import React, { useMemo, useState } from 'react';
import { Customer, Quote } from '../../types';
import {
  AcceptFrameworkValues,
  frameworkAcceptDefaults,
  frameworkContractLabel,
  frameworkItemNetUnitPrice,
  frameworkItemsFromQuote,
} from '../../services/frameworkContractLogic';
import { formatEur } from '../../services/quoteOrderTranches';
import { Button } from '../Button';
import { Loader2, AlertTriangle, Info } from 'lucide-react';

/**
 * Annahme eines Angebots ohne Endsumme als Rahmenvertrag: es entsteht KEIN
 * Projekt, die vereinbarten Einheitspreise landen beim Kunden. Rumpf und
 * Fusszeile des Annahmedialogs im Modus „Rahmenvertrag" — Kopf und
 * Moduswahl stellt AcceptQuoteDialog.
 */
interface Props {
  quote: Quote;
  customer: Customer;
  busy?: boolean;
  error?: string;
  onCancel: () => void;
  onConfirm: (values: AcceptFrameworkValues) => void;
}

const inputClass =
  'w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1';

const formatPercent = (p: number): string =>
  `${p.toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;

export const AcceptFrameworkForm: React.FC<Props> = ({ quote, customer, busy, error, onCancel, onConfirm }) => {
  const [values, setValues] = useState<AcceptFrameworkValues>(() =>
    frameworkAcceptDefaults(quote, new Date().toISOString()));
  const set = <K extends keyof AcceptFrameworkValues>(k: K, v: AcceptFrameworkValues[K]) =>
    setValues((prev) => ({ ...prev, [k]: v }));

  // Genau die Positionen, die der Vertrag bekommt (gestrichene entfallen).
  const items = useMemo(() => frameworkItemsFromQuote(quote), [quote]);
  // Abschnittsueberschrift nur beim Wechsel zeigen, damit die Vorschau
  // gegliedert bleibt wie das Angebot.
  const rows = useMemo(
    () => items.map((it, i) => ({
      it,
      header: it.sectionTitle && it.sectionTitle !== items[i - 1]?.sectionTitle ? it.sectionTitle : undefined,
    })),
    [items],
  );
  const totalDiscount = Number(quote.discountPercent) || 0;

  // Hinweise aus dem Kundenstamm — warnen, nicht sperren: eine doppelte
  // Nummer kann gewollt sein (Verlaengerung), der Standard ist bewusst
  // einer je Kunde.
  const existing = customer.frameworkContracts || [];
  const effectiveNumber = (values.contractNumber.trim() || values.customerRef?.trim() || '').toLowerCase();
  const numberExists = !!effectiveNumber
    && existing.some((c) => (c.number || '').trim().toLowerCase() === effectiveNumber);
  const currentDefault = existing.find((c) => c.isDefault);

  // Zeitraum verkehrt herum = Vertrag, der nie gilt.
  const rangeInvalid = !!values.validFrom && !!values.validUntil && values.validUntil < values.validFrom;
  const canConfirm = items.length > 0 && !rangeInvalid && !busy;

  return (
    <>
      <div className="p-6 space-y-4">
        <div className="flex gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            Es wird kein Projekt angelegt. Die Preise liegen danach beim Kunden unter
            Konditionen → Rahmenverträge und lassen sich bei der Projektanlage und in
            neuen Angeboten übernehmen.
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Vertragsnummer</label>
            <input className={inputClass} value={values.contractNumber}
                   placeholder="leer = Bestellnummer, sonst Auftragsnummer"
                   onChange={(e) => set('contractNumber', e.target.value)} />
            {numberExists && (
              <p className="text-xs text-amber-700 mt-1 ml-1 font-semibold">
                Beim Kunden gibt es bereits einen Rahmenvertrag mit dieser Nummer.
              </p>
            )}
          </div>
          <div>
            <label className={labelClass}>Bezeichnung</label>
            <input className={inputClass} value={values.title}
                   onChange={(e) => set('title', e.target.value)} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Gültig von</label>
            <input type="date" className={inputClass} value={values.validFrom}
                   onChange={(e) => set('validFrom', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Gültig bis</label>
            <input type="date" className={inputClass} value={values.validUntil}
                   onChange={(e) => set('validUntil', e.target.value)} />
            <p className="text-xs text-slate-500 mt-1 ml-1">leer = unbefristet</p>
          </div>
        </div>
        {rangeInvalid && (
          <p className="text-xs font-semibold text-red-600 -mt-2 ml-1">„Gültig bis“ liegt vor „Gültig von“.</p>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Bestellnummer Kunde</label>
            <input className={inputClass} value={values.customerRef || ''} placeholder="optional"
                   onChange={(e) => set('customerRef', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Beauftragt am</label>
            <input type="date" className={inputClass} value={values.orderDate || ''}
                   onChange={(e) => set('orderDate', e.target.value)} />
          </div>
        </div>

        <div>
          <label className={labelClass}>Notiz</label>
          <textarea className={`${inputClass} min-h-[64px]`} rows={2} value={values.note || ''} placeholder="optional"
                    onChange={(e) => set('note', e.target.value)} />
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={values.makeDefault}
                 onChange={(e) => set('makeDefault', e.target.checked)} />
          <span className="text-sm text-slate-700">
            Als Standard-Rahmenvertrag (neue Angebote belegen ihn vor)
            {values.makeDefault && currentDefault && (
              <span className="block text-xs text-slate-500">
                Löst „{frameworkContractLabel(currentDefault)}“ als Standard ab.
              </span>
            )}
          </span>
        </label>

        {/* Vorschau: was beim Kunden hinterlegt wird. EP netto ist der
            vereinbarte Preis, der spaeter ins LV bzw. in neue Angebote geht. */}
        <div>
          <label className={labelClass}>Preispositionen ({items.length})</label>
          {items.length === 0 ? (
            <p className="text-xs font-semibold text-amber-700 mt-1 ml-1">
              Das Angebot hat keine (nicht gestrichenen) Positionen — es gibt nichts zu hinterlegen.
            </p>
          ) : (
            <div className="mt-1 border border-slate-200 rounded-xl overflow-auto max-h-64">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Pos</th>
                    <th className="text-left px-3 py-2">Bezeichnung</th>
                    <th className="text-left px-3 py-2">Einheit</th>
                    <th className="text-right px-3 py-2 hidden sm:table-cell">EP</th>
                    <th className="text-right px-3 py-2 hidden sm:table-cell">Rabatt</th>
                    <th className="text-right px-3 py-2 whitespace-nowrap">EP netto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map(({ it, header }) => (
                    <React.Fragment key={it.id}>
                      {header && (
                        <tr className="bg-slate-50/60">
                          <td colSpan={6} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">{header}</td>
                        </tr>
                      )}
                      <tr className="text-slate-700">
                        <td className="px-3 py-1.5 font-mono text-slate-400 whitespace-nowrap">{it.posNumber || it.lvPosition}</td>
                        <td className="px-3 py-1.5">
                          {it.title}
                          {it.nep && (
                            <span className="ml-1.5 text-[10px] font-bold text-amber-600" title="Eventualposition: Ausführung nur auf gesonderte Anforderung">NEP</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap">{it.unit}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap hidden sm:table-cell">{formatEur(it.unitPrice)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap hidden sm:table-cell">
                          {it.discountPercent ? formatPercent(it.discountPercent) : '—'}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap font-semibold">
                          {formatEur(frameworkItemNetUnitPrice(it, quote.discountPercent))}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {items.length > 0 && totalDiscount > 0 && (
            <p className="text-xs text-slate-500 mt-1 ml-1">EP netto inkl. {formatPercent(totalDiscount)} Gesamtrabatt.</p>
          )}
        </div>

        {error && (
          <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>Abbrechen</Button>
        <Button onClick={() => onConfirm({ ...values })} disabled={!canConfirm}
                title={items.length === 0 ? 'Keine Positionen zum Hinterlegen.' : undefined}>
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> wird hinterlegt…</> : 'Rahmenvertrag hinterlegen'}
        </Button>
      </div>
    </>
  );
};
